import { c as _c } from "react/compiler-runtime";
import React from "react";
import { RichText } from "@payloadcms/richtext-lexical/react";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { cn } from "@/utilities/core/cn";
import type { Media } from "@/payload-types";
type MediaCaptionProps = {
  media: Media | null | undefined;
  /** Optional override caption (e.g. from a MediaBlock). Falls back to media.caption. */
  caption?: Media["caption"];
  className?: string;
};
type LexicalNode = {
  type?: string;
  text?: string;
  children?: LexicalNode[];
};
function hasTextContent(node: LexicalNode | undefined | null): boolean {
  if (!node) return false;
  if (typeof node.text === "string" && node.text.trim().length > 0) return true;
  if (Array.isArray(node.children)) {
    return node.children.some(hasTextContent);
  }
  return false;
}
function isCaptionEmpty(caption: Media["caption"]): boolean {
  if (!caption || typeof caption !== "object") return true;
  const root = (caption as {
    root?: LexicalNode;
  }).root;
  return !hasTextContent(root);
}

/**
 * Renders a subtle caption for a Media item. Returns null when the caption is
 * missing or contains no text, so it can be dropped in unconditionally.
 */
export function MediaCaption(t0) {
  const $ = _c(7);
  const {
    media,
    caption,
    className
  } = t0;
  const resolved = caption && !isCaptionEmpty(caption) ? caption : media?.caption;
  if (!resolved || isCaptionEmpty(resolved)) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("mt-2 text-sm text-muted-foreground/80 italic leading-snug [&_p]:m-0 [&_a]:underline", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = resolved as SerializedEditorState;
  let t3;
  if ($[2] !== t2) {
    t3 = <RichText data={t2} />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t3) {
    t4 = <div className={t1}>{t3}</div>;
    $[4] = t1;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}